import { Injectable, OnDestroy } from '@angular/core';

import { Router } from '@angular/router';

import { BehaviorSubject, Subject, Subscription } from 'rxjs';

import { StoresService } from './stores.service';



export interface IntegrationStepConfig {
  connectPlatform: boolean;
  addAiKnowledge: boolean;
  uploadCatalog: boolean;
  createBot: boolean;
}

export const INTEGRATION_STEPS_BY_ACTIVITY: Record<
  string,
  Record<string, IntegrationStepConfig>
> = {
  'e-commerce': {
    shopify: {
      connectPlatform: true,
      addAiKnowledge: true,
      uploadCatalog: false,
      createBot: true,
    },
    bigcommerce: {
      connectPlatform: true,
      addAiKnowledge: true,
      uploadCatalog: false,
      createBot: true,
    },
    prestashop: {
      connectPlatform: true,
      addAiKnowledge: true,
      uploadCatalog: false,
      createBot: true,
    },
    woocommerce: {
      connectPlatform: true,
      addAiKnowledge: true,
      uploadCatalog: false,
      createBot: true,
    },
    salla: {
      connectPlatform: true,
      addAiKnowledge: true,
      uploadCatalog: false,
      createBot: true,
    },
    other: {
      connectPlatform: false,
      addAiKnowledge: true,
      uploadCatalog: true,
      createBot: true,
    },
  },
  other: {
    wordpress: {
      connectPlatform: true,
      addAiKnowledge: true,
      uploadCatalog: false,
      createBot: true,
    },
    other: {
      connectPlatform: false,
      addAiKnowledge: true,
      uploadCatalog: true,
      createBot: true,
    },
  },
};

export function getIntegrationStepConfig(
  activity?: string | null,
  platform?: string | null,
): IntegrationStepConfig | null {
  if (!activity || !platform) {
    return null;
  }
  const normalizedPlatform = platform.trim().toLowerCase();
  return (
    INTEGRATION_STEPS_BY_ACTIVITY[activity]?.[normalizedPlatform] ?? null
  );
}

export function isEcommerceAutoCatalogSync(
  activity?: string | null,
  config?: IntegrationStepConfig | null,
): boolean {
  return (
    activity === 'e-commerce' &&
    !!config?.connectPlatform &&
    !config.uploadCatalog
  );
}



export type CatalogSyncStatus = 'idle' | 'loading' | 'ready' | 'failed';



export interface OnboardingTourStep {

  id: string;

  stepNumber: number;

  title: string;

  description: string;

  anchorSelector: string;

  placement: 'top' | 'bottom' | 'left' | 'right';

  prepareAction?: 'scroll-integration-panel' | 'open-workspace-panel';

  /** When true, user can click through the overlay to use highlighted controls. */
  allowPageInteraction?: boolean;

  /** Gate type checked before allowing Next / Finish. */
  gate?: 'store-installed' | 'bot-created' | 'catalog-ready';

}



export interface CatalogWatchContext {

  platform: string;

  storeId: string;

  shopActivity?: string | null;

  storeInstalled: boolean;

}



interface TourContext {

  shopActivity: string | null;

  platform: string | null;

  stepConfig: IntegrationStepConfig;

}



type NormalizedCatalogStatus = 'loading' | 'ready' | 'failed' | 'unknown';



export type OnboardingTourScope = 'integration' | 'activation';



export interface TourRuntimeContext {

  storeInstalled: boolean;

  hasBots: boolean;

}



export interface TourGateState {

  canProceed: boolean;

  message: string | null;

}



@Injectable({ providedIn: 'root' })

export class EcommerceOnboardingService implements OnDestroy {

  readonly catalogSyncStatus$ = new BehaviorSubject<CatalogSyncStatus>('idle');

  readonly catalogSyncMessage$ = new BehaviorSubject<string>('');

  readonly tourActive$ = new BehaviorSubject<boolean>(false);

  readonly currentStepIndex$ = new BehaviorSubject<number>(0);

  readonly activeSteps$ = new BehaviorSubject<OnboardingTourStep[]>([]);

  readonly prepareStep$ = new Subject<OnboardingTourStep>();

  readonly tourGateState$ = new BehaviorSubject<TourGateState>({

    canProceed: true,

    message: null,

  });

  /** Bot toggled ON during activation tour — switches card to Save & Finish UX. */
  readonly activationTourCommitted$ = new BehaviorSubject<boolean>(false);

  readonly activationSaveAndFinish$ = new Subject<void>();



  private workspaceId: string | null = null;

  private catalogSub: Subscription | null = null;

  private watchContext: CatalogWatchContext | null = null;

  private tourContext: TourContext | null = null;

  private activeSteps: OnboardingTourStep[] = [];

  private runtimeContext: TourRuntimeContext = {

    storeInstalled: false,

    hasBots: false,

  };

  private tourDisplayTotalSteps = 0;

  private lastKnownStoreInstalled = false;

  private activeTourScope: OnboardingTourScope | null = null;



  private readonly destroy$ = new Subject<void>();



  constructor(

    private storesService: StoresService,

    private router: Router,

  ) {}



  ngOnDestroy(): void {

    this.stopCatalogWatch();

    this.destroy$.next();

    this.destroy$.complete();

  }



  get steps(): OnboardingTourStep[] {

    return this.activeSteps;

  }



  get totalSteps(): number {

    return this.activeSteps.length;

  }



  get effectiveTotalSteps(): number {

    return this.tourDisplayTotalSteps || this.activeSteps.length;

  }



  get currentStep(): OnboardingTourStep | null {

    const index = this.currentStepIndex$.value;

    return this.activeSteps[index] ?? null;

  }



  isEcommerceActivity(activity?: string | null): boolean {

    return activity === 'e-commerce';

  }



  isEcommerceAutoSyncWorkspace(): boolean {

    return isEcommerceAutoCatalogSync(

      this.tourContext?.shopActivity,

      this.tourContext?.stepConfig,

    );

  }



  resolveStoreInstalled(platform: string, status?: unknown): boolean {

    if (this.platformRequiresInstallCheck(platform)) {

      return (status as string[] | undefined)?.[0] === 'installed';

    }

    return true;

  }



  setWorkspaceContext(workspaceId: string | null): void {

    this.workspaceId = workspaceId;

  }



  configureTour(shopActivity?: string | null, platform?: string | null): void {

    const stepConfig = getIntegrationStepConfig(shopActivity, platform);

    if (!stepConfig) {

      this.tourContext = null;

      this.activeSteps = [];

      this.activeSteps$.next([]);

      return;

    }



    this.tourContext = {

      shopActivity: shopActivity ?? null,

      platform: platform ?? null,

      stepConfig,

    };

    this.activeSteps = this.buildTourSteps(

      stepConfig,

      shopActivity ?? '',

      { omitInstallStep: this.runtimeContext.storeInstalled },

    );

    this.tourDisplayTotalSteps = this.resolveTourDisplayTotalSteps(

      stepConfig,

      shopActivity ?? '',

    );

    this.activeSteps$.next(this.activeSteps);

  }



  /** Single-step tour for bot activation on widget settings page (shown as last step). */
  configureActivationTour(shopActivity?: string | null, platform?: string | null): void {

    const stepConfig = getIntegrationStepConfig(shopActivity, platform);

    const fallbackConfig: IntegrationStepConfig = {

      connectPlatform: false,

      addAiKnowledge: false,

      uploadCatalog: false,

      createBot: false,

    };

    this.tourContext = {

      shopActivity: shopActivity ?? null,

      platform: platform ?? null,

      stepConfig: stepConfig ?? fallbackConfig,

    };

    this.tourDisplayTotalSteps = this.resolveTourDisplayTotalSteps(

      stepConfig ?? fallbackConfig,

      shopActivity ?? '',

    );

    this.activeSteps = [

      {

        ...this.buildBotActivationStep(),

        stepNumber: this.tourDisplayTotalSteps,

      },

    ];

    this.activeSteps$.next(this.activeSteps);

  }



  updateTourRuntimeContext(ctx: Partial<TourRuntimeContext>): void {

    const wasInstalled = this.runtimeContext.storeInstalled;

    this.runtimeContext = { ...this.runtimeContext, ...ctx };

    if (

      ctx.storeInstalled !== undefined &&

      ctx.storeInstalled !== wasInstalled &&

      ctx.storeInstalled

    ) {

      this.refreshIntegrationStepsForInstallState();

    } else if (

      ctx.storeInstalled &&

      this.activeSteps.some((step) => step.id === 'install-platform')

    ) {

      this.refreshIntegrationStepsForInstallState();

    }

    this.skipInstallStepIfNeeded();

    this.refreshTourGateState();

  }



  /** Resume integration tour at install, or first post-install step when app is already connected. */
  resolveIntegrationTourStartIndex(): number {

    const steps = this.activeSteps;

    if (!steps.length) {

      return -1;

    }

    if (this.runtimeContext.hasBots) {

      return -1;

    }

    const indexOf = (id: string) => steps.findIndex((step) => step.id === id);

    const installIdx = indexOf('install-platform');

    if (installIdx >= 0 && !this.runtimeContext.storeInstalled) {

      return installIdx;

    }

    for (const stepId of [

      'add-ai-knowledge',

      'upload-catalog',

      'workspace-settings',

      'create-bot',

    ]) {

      const idx = indexOf(stepId);

      if (idx >= 0) {

        return idx;

      }

    }

    return steps.length > 0 ? 0 : -1;

  }



  /** Drop install step from integration tour when platform is already connected. */
  private refreshIntegrationStepsForInstallState(): void {

    if (!this.tourContext?.stepConfig) {

      return;

    }

    if (this.activeTourScope === 'activation') {

      return;

    }

    const currentStepId = this.currentStep?.id;

    const steps = this.buildTourSteps(

      this.tourContext.stepConfig,

      this.tourContext.shopActivity ?? '',

      { omitInstallStep: this.runtimeContext.storeInstalled },

    );

    this.activeSteps = steps;

    this.tourDisplayTotalSteps = this.resolveTourDisplayTotalSteps(

      this.tourContext.stepConfig,

      this.tourContext.shopActivity ?? '',

    );

    this.activeSteps$.next(steps);

    if (!this.tourActive$.value || !currentStepId) {

      return;

    }

    if (currentStepId === 'install-platform') {

      this.skipInstallStepIfNeeded();

      return;

    }

    const remappedIndex = steps.findIndex((step) => step.id === currentStepId);

    if (remappedIndex >= 0) {

      this.currentStepIndex$.next(remappedIndex);

    }

  }



  private skipInstallStepIfNeeded(): void {

    if (

      !this.tourActive$.value ||

      this.activeTourScope !== 'integration' ||

      !this.runtimeContext.storeInstalled

    ) {

      return;

    }

    if (this.currentStep?.id !== 'install-platform') {

      return;

    }

    const nextIndex = this.resolveIntegrationTourStartIndex();

    if (nextIndex >= 0) {

      this.currentStepIndex$.next(nextIndex);

    }

  }



  canProceedFromCurrentStep(): boolean {

    return this.canProceedFromStep(this.currentStep);

  }



  canProceedFromStep(step: OnboardingTourStep | null): boolean {

    if (!step) {

      return false;

    }

    switch (step.gate) {

      case 'store-installed':

        return this.runtimeContext.storeInstalled;

      case 'bot-created':

        return this.runtimeContext.hasBots;

      case 'catalog-ready':

        if (!this.isEcommerceAutoSyncWorkspace()) {

          return true;

        }

        return this.isCatalogReady();

      default:

        return true;

    }

  }



  getStepBlockedMessage(step: OnboardingTourStep | null): string | null {

    if (!step || this.canProceedFromStep(step)) {

      return null;

    }

    switch (step.gate) {

      case 'store-installed':

        return 'Install the app on your platform first — the Next button unlocks once the connection is detected.';

      case 'bot-created':

        return 'Create your bot first, then you can continue to activation.';

      case 'catalog-ready':

        return 'Your product catalog is still uploading — activation unlocks once the upload is complete.';

      default:

        return null;

    }

  }



  refreshTourGateState(): void {

    const message = this.getStepBlockedMessage(this.currentStep);

    this.tourGateState$.next({

      canProceed: this.canProceedFromStep(this.currentStep),

      message,

    });

  }



  onStoreInstallStatusChanged(storeInstalled: boolean): void {

    this.lastKnownStoreInstalled = storeInstalled;

    this.updateTourRuntimeContext({ storeInstalled });

  }



  /** Subscribe to Firestore catalog status for the active e-commerce workspace. */

  bindCatalogWatch(context: CatalogWatchContext | null): void {

    this.watchContext = context;

    this.stopCatalogWatch();



    if (

      !context ||

      !context.platform ||

      !context.storeId ||

      !this.isEcommerceActivity(context.shopActivity) ||

      !context.storeInstalled

    ) {

      this.setCatalogState('idle');

      return;

    }



    const path = `${context.platform}/${context.storeId}/catalogs`;

    this.catalogSub = this.storesService.getStoreCatalogs(path).subscribe({

      next: (catalogs) => this.applyCatalogStatuses(catalogs ?? []),

      error: () => this.setCatalogState('idle'),

    });

  }



  stopCatalogWatch(): void {

    if (this.catalogSub) {

      this.catalogSub.unsubscribe();

      this.catalogSub = null;

    }

  }



  startTour(fromStep = 0): void {

    if (!this.activeSteps.length || fromStep < 0) {

      return;

    }

    this.currentStepIndex$.next(fromStep);

    this.tourActive$.next(true);

    this.refreshTourGateState();

  }



  startIntegrationTour(fromStep = 0): void {

    if (!EcommerceOnboardingService.isWorkspaceSettingsUrl(this.router.url)) {

      return;

    }

    this.activeTourScope = 'integration';

    this.startTour(fromStep);

  }



  startActivationTour(fromStep = 0): void {

    if (!EcommerceOnboardingService.isBotActivationSettingsUrl(this.router.url)) {

      return;

    }

    this.activeTourScope = 'activation';

    this.startTour(fromStep);

  }



  static isWorkspaceSettingsUrl(url: string): boolean {

    return /\/(?:admin\/)?stores\/[^/?#]+\/details(?:[/?#]|$)/.test(url);

  }



  static isBotActivationSettingsUrl(url: string): boolean {

    return /\/design\/[^/?#]+\/settings\/[^/?#]+(?:[/?#]|$)/.test(url);

  }



  canStartIntegrationTour(url: string): boolean {

    return EcommerceOnboardingService.isWorkspaceSettingsUrl(url);

  }



  canStartActivationTour(url: string): boolean {

    return EcommerceOnboardingService.isBotActivationSettingsUrl(url);

  }



  isIntegrationTourActive(): boolean {

    return this.tourActive$.value && this.activeTourScope === 'integration';

  }



  isActivationTourActive(): boolean {

    return this.tourActive$.value && this.activeTourScope === 'activation';

  }



  isActivationTourCommitted(): boolean {

    return this.activationTourCommitted$.value;

  }



  setActivationTourCommitted(committed: boolean): void {

    this.activationTourCommitted$.next(committed);

  }



  requestActivationSaveAndFinish(): void {

    this.activationSaveAndFinish$.next();

  }



  private resetActivationTourUiState(): void {

    this.activationTourCommitted$.next(false);

  }



  static isTourAllowedOnUrl(

    url: string,

    scope: OnboardingTourScope | null,

  ): boolean {

    if (!scope) {

      return false;

    }

    if (scope === 'integration') {

      return EcommerceOnboardingService.isWorkspaceSettingsUrl(url);

    }

    return EcommerceOnboardingService.isBotActivationSettingsUrl(url);

  }



  shouldShowTourOverlay(url = this.router.url): boolean {

    return (

      this.tourActive$.value &&

      EcommerceOnboardingService.isTourAllowedOnUrl(url, this.activeTourScope)

    );

  }



  enforceTourRouteScope(url: string): void {

    if (!this.tourActive$.value) {

      return;

    }

    if (!EcommerceOnboardingService.isTourAllowedOnUrl(url, this.activeTourScope)) {

      this.stopTour();

    }

  }



  stopTour(): void {

    this.resetActivationTourUiState();

    this.tourActive$.next(false);

    this.activeTourScope = null;

  }



  /** Dismiss guide — always shows again on next visit (only Finish marks done). */

  skipTour(): void {

    this.clearLegacyTourStorage();

    this.currentStepIndex$.next(0);

    this.stopTour();

  }



  private clearLegacyTourStorage(): void {

    if (!this.workspaceId) {

      return;

    }

    localStorage.removeItem(this.getTourStorageKey(this.workspaceId));

    localStorage.removeItem(`ecom_onboarding_tour_${this.workspaceId}`);

    localStorage.removeItem(`onboarding_tour_finished_${this.workspaceId}`);

    localStorage.removeItem(this.getTourFinishedStorageKey(this.workspaceId));

  }



  completeTour(): void {

    this.clearLegacyTourStorage();

    this.currentStepIndex$.next(0);

    this.stopTour();

  }



  shouldAutoStartGuide(
    workspaceId?: string | null,
    bots?: any[] | null,
    userStores?: any[] | null,
  ): boolean {

    if (!workspaceId) {

      return false;

    }

    if (!this.hasSingleWorkspace(userStores)) {

      return false;

    }

    this.setWorkspaceContext(workspaceId);

    this.clearLegacyTourStorage();

    return !this.hasActivatedBot(bots ?? []);

  }



  /** Guide only for accounts with one workspace (multi-workspace users already know the flow). */
  hasSingleWorkspace(userStores?: any[] | null): boolean {

    const stores = (userStores ?? []).filter((store) => store?.id);

    return stores.length === 1;

  }



  /** First workspace in the account list (same order as sidebar `stores[0]`). */
  resolveFirstWorkspaceId(userStores?: any[] | null): string | null {

    const stores = (userStores ?? []).filter((store) => store?.id);

    if (!stores.length) {

      return null;

    }

    return String(stores[0].id);

  }



  isFirstWorkspace(
    workspaceId?: string | null,
    userStores?: any[] | null,
  ): boolean {

    const firstWorkspaceId = this.resolveFirstWorkspaceId(userStores);

    if (!firstWorkspaceId) {

      return false;

    }

    return String(workspaceId) === firstWorkspaceId;

  }



  hasActivatedBot(bots: any[]): boolean {

    return (bots ?? []).some((bot) => this.isBotActivated(bot));

  }



  hasAnyBot(bots: any[] | null | undefined): boolean {

    return (bots ?? []).length > 0;

  }



  isBotActivated(bot: any): boolean {

    if (!bot) {

      return false;

    }

    if (bot.onOff === true) {

      return true;

    }

    if (bot.botActivation === true || bot.botActivation === 'true') {

      return true;

    }

    return false;

  }



  isTourCompleted(
    workspaceId?: string | null,
    bots?: any[] | null,
    userStores?: any[] | null,
  ): boolean {

    return !this.shouldAutoStartGuide(workspaceId, bots, userStores);

  }



  getTourStorageKey(workspaceId: string): string {

    return `onboarding_tour_${workspaceId}`;

  }



  getTourFinishedStorageKey(workspaceId: string): string {

    return `conversell_integration_guide_done_${workspaceId}`;

  }



  nextStep(): void {

    if (!this.canProceedFromCurrentStep()) {

      return;

    }

    const next = this.currentStepIndex$.value + 1;

    if (next >= this.activeSteps.length) {

      this.completeTour();

      return;

    }

    this.currentStepIndex$.next(next);

    this.refreshTourGateState();

  }



  previousStep(): void {

    let prev = this.currentStepIndex$.value - 1;

    while (prev >= 0) {

      const step = this.activeSteps[prev];

      if (

        step?.id === 'install-platform' &&

        this.runtimeContext.storeInstalled

      ) {

        prev--;

        continue;

      }

      break;

    }

    this.currentStepIndex$.next(Math.max(0, prev));

    this.refreshTourGateState();

  }



  goToStep(stepId: string): void {

    const index = this.activeSteps.findIndex((s) => s.id === stepId);

    if (index < 0) {

      return;

    }

    if (

      stepId === 'bot-activation' &&

      !EcommerceOnboardingService.isBotActivationSettingsUrl(this.router.url)

    ) {

      return;

    }

    if (

      stepId !== 'bot-activation' &&

      !EcommerceOnboardingService.isWorkspaceSettingsUrl(this.router.url)

    ) {

      return;

    }

    this.currentStepIndex$.next(index);

    this.tourActive$.next(true);

    this.refreshTourGateState();

  }



  isCatalogReady(): boolean {

    return this.catalogSyncStatus$.value === 'ready';

  }



  isCatalogLoading(): boolean {

    return this.catalogSyncStatus$.value === 'loading';

  }



  isCatalogFailed(): boolean {

    return this.catalogSyncStatus$.value === 'failed';

  }



  isCatalogBlocking(): boolean {

    const status = this.catalogSyncStatus$.value;

    return status === 'loading' || status === 'failed';

  }



  canActivateBot(): boolean {

    if (this.isEcommerceAutoSyncWorkspace()) {

      return !this.isCatalogBlocking();

    }

    return true;

  }



  canCreateBot(storeInstalled: boolean): boolean {

    return storeInstalled;

  }



  private buildTourSteps(

    config: IntegrationStepConfig,

    shopActivity: string,

    options: { omitInstallStep?: boolean } = {},

  ): OnboardingTourStep[] {

    const steps: OnboardingTourStep[] = [];

    let stepNumber = 0;

    const isEcommerce = shopActivity === 'e-commerce';

    const isCustomPlatform = this.tourContext?.platform === 'other';

    const omitInstallStep = !!options.omitInstallStep;

    const skippedInstallSteps =

      omitInstallStep && config.connectPlatform ? 1 : 0;



    const push = (step: Omit<OnboardingTourStep, 'stepNumber'>) => {

      stepNumber++;

      steps.push({ ...step, stepNumber: stepNumber + skippedInstallSteps });

    };



    // Workspace is created by default at signup — step kept for reference only.
    /*
    push({

      id: 'create-workspace',

      title: 'Create your workspace',

      description:

        'Use + Add new Workspace in the sidebar to create and manage your bots, catalog, and settings.',

      anchorSelector: '[data-onboarding="create-workspace"]',

      placement: 'right',

      prepareAction: 'open-workspace-panel',

    });
    */



    if (config.connectPlatform && !omitInstallStep) {

      push({

        id: 'install-platform',

        title: isEcommerce ? 'Install the app' : 'Connect your platform',

        description: isEcommerce

          ? 'Connect your store by installing the Conversell app on your e-commerce platform.'

          : 'Link your website platform to enable integrations and continue setup.',

        anchorSelector: '[data-onboarding="install-platform"]',

        placement: 'bottom',

        prepareAction: 'scroll-integration-panel',

        allowPageInteraction: true,

        gate: 'store-installed',

      });

    }



    if (config.addAiKnowledge) {

      push({

        id: 'add-ai-knowledge',

        title: isEcommerce && !isCustomPlatform ? 'Enrich your AI knowledge' : 'Add your AI knowledge',

        description:

          isEcommerce && !isCustomPlatform

            ? 'Your products sync automatically. Add FAQs, policies, or support docs to boost answers.'

            : 'Upload FAQs, product info, or business documents to power accurate bot responses.',

        anchorSelector: '[data-onboarding="add-ai-knowledge"]',

        placement: 'bottom',

        prepareAction: 'scroll-integration-panel',

        allowPageInteraction: true,

      });

    }



    if (config.uploadCatalog) {

      push({

        id: 'upload-catalog',

        title: 'Upload your product catalog',

        description:

          'Add your product catalog so your bot can suggest items, answer pricing questions, and boost sales.',

        anchorSelector: '[data-onboarding="upload-catalog"]',

        placement: 'bottom',

        prepareAction: 'scroll-integration-panel',

        allowPageInteraction: true,

      });

    }



    push({

      id: 'workspace-settings',

      title: 'Configure workspace settings',

      description:

        'Complete your workspace identity, description, and Human Assistance settings.',

      anchorSelector: '[data-onboarding="workspace-settings"]',

      placement: 'bottom',

      prepareAction: 'scroll-integration-panel',

      allowPageInteraction: true,

    });



    if (config.createBot) {

      push({

        id: 'create-bot',

        title: 'Create your bot',

        description:

          'Build conversation flows to handle common questions and automate customer interactions.',

        anchorSelector: '[data-onboarding="create-bot"]',

        placement: 'top',

        prepareAction: 'scroll-integration-panel',

        allowPageInteraction: true,

        gate: 'bot-created',

      });

    }

    return steps;

  }



  private buildBotActivationStep(): OnboardingTourStep {

    return {

      id: 'bot-activation',

      stepNumber: 1,

      title: 'Activate your bot',

      description:

        'Turn on your bot to make it live on your channel once setup is complete.',

      anchorSelector: '[data-onboarding="bot-activation"]',

      placement: 'bottom',

      allowPageInteraction: true,

      gate: 'catalog-ready',

    };

  }



  /** Full journey step count (includes skipped install) + activation. */
  private resolveTourDisplayTotalSteps(

    config: IntegrationStepConfig,

    shopActivity: string,

  ): number {

    return (

      this.buildTourSteps(config, shopActivity, { omitInstallStep: false }).length +

      1

    );

  }



  private applyCatalogStatuses(catalogs: any[]): void {

    const englishCatalogs = this.getEnglishCatalogs(catalogs);



    if (!englishCatalogs.length) {

      this.setCatalogState(catalogs.length === 0 ? 'loading' : 'idle');

      return;

    }



    const normalized = englishCatalogs.map((catalog) =>

      this.normalizeCatalogStatus(catalog?.status),

    );



    if (normalized.some((status) => status === 'failed')) {

      this.setCatalogState(

        'failed',

        this.buildFailedMessage(englishCatalogs, normalized),

      );

      return;

    }



    if (normalized.some((status) => status === 'loading')) {

      this.setCatalogState('loading');

      return;

    }



    if (normalized.every((status) => status === 'ready')) {

      this.setCatalogState('ready');

      return;

    }



    this.setCatalogState('idle');

  }



  /** French catalogs stay Draft permanently — only English drives sync status. */

  private getEnglishCatalogs(catalogs: any[]): any[] {

    return catalogs.filter((catalog) => this.isEnglishCatalog(catalog));

  }



  private isEnglishCatalog(catalog: any): boolean {

    const lang = (catalog?.language ?? '').trim().toLowerCase();

    return lang === 'en' || lang.startsWith('en-');

  }



  private normalizeCatalogStatus(status?: string): NormalizedCatalogStatus {

    const value = (status ?? '').trim().toLowerCase();

    if (value === 'failed') {

      return 'failed';

    }

    if (value === 'draft' || value === 'pending') {

      return 'loading';

    }

    if (

      value === 'succefull' ||

      value === 'successful' ||

      value === 'ready' ||

      value === 'success'

    ) {

      return 'ready';

    }

    return 'unknown';

  }



  private buildFailedMessage(

    catalogs: any[],

    normalized: NormalizedCatalogStatus[],

  ): string {

    const failedCatalog = catalogs.find(

      (_, index) => normalized[index] === 'failed',

    );

    const detail =

      failedCatalog?.errorMessage ||

      failedCatalog?.failureReason ||

      failedCatalog?.error ||

      failedCatalog?.message;



    if (typeof detail === 'string' && detail.trim()) {

      return detail.trim();

    }



    return 'Catalog upload failed. Open Product Catalogs to review the issue and try again.';

  }



  private setCatalogState(

    status: CatalogSyncStatus,

    message = '',

  ): void {

    this.catalogSyncMessage$.next(message);

    this.catalogSyncStatus$.next(status);

    this.refreshTourGateState();

  }



  private platformRequiresInstallCheck(platform: string): boolean {

    return platform === 'shopify' || platform === 'salla';

  }

}


